import { ApiRequestException, ApiResponse } from '@/models/apiResponse'
import { Group } from '@/models/group'
import { StoreInfra } from '@/models/operations/types'
import { GroupService } from '@/services/group'

import { normalizeStoreInfra } from './storeUtils'

export function isApiRequestException(error: unknown): error is ApiRequestException {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as ApiRequestException).error?.status === 'number'
  )
}

export function getApiErrorMessage(response: ApiResponse<unknown>, fallback: string): string {
  if (!response.error) return fallback
  const { status, content, statusText } = response.error
  const detail = typeof content === 'string' && content.trim() ? content : statusText || null
  return status ? `${fallback} (${status})${detail ? `: ${detail}` : ''}` : fallback
}

/** Matches how other modules read errors thrown by sendRequest (ApiRequestException). */
export function resolveRequestError(error: unknown, fallback: string): string {
  if (isApiRequestException(error)) {
    return getApiErrorMessage(error, fallback)
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message
  }
  return fallback
}

export async function fetchAllGroupsMetadata(): Promise<Group[]> {
  try {
    const response = await GroupService.getAllGroups()
    if (response.error || !response.data) {
      throw new Error(getApiErrorMessage(response, 'Failed to fetch groups'))
    }
    return response.data
  } catch (error) {
    throw new Error(resolveRequestError(error, 'Failed to fetch groups'))
  }
}

export async function fetchStoreInfraByGroup(groupId: string): Promise<StoreInfra[]> {
  try {
    const response = await GroupService.getGroupStoreInfra(groupId)
    if (response.error || !response.data) {
      throw new Error(getApiErrorMessage(response, 'Failed to load stores for this group'))
    }
    return (response.data ?? []).map((store) => normalizeStoreInfra(store))
  } catch (error) {
    throw new Error(resolveRequestError(error, 'Failed to load stores for this group'))
  }
}

export async function fetchStoreInfraById(
  groupId: string,
  accountId: string,
  storeId: string,
): Promise<StoreInfra> {
  try {
    const response = await GroupService.getGroupStoreInfra(groupId, { accountId, storeId })
    if (response.error || !response.data?.length) {
      throw new Error(getApiErrorMessage(response, 'Failed to load store'))
    }
    return normalizeStoreInfra(response.data[0])
  } catch (error) {
    throw new Error(resolveRequestError(error, 'Failed to load store'))
  }
}

export function toOpsGroupBase(group: Group) {
  return {
    id: group.id,
    name: group.name,
    description: group.description,
  }
}
